import { Box } from '@mui/material';
import { Spinner } from 'components/core';
import type { ChartId } from '../catalog/chartCatalog';
import { ChartResultStatus } from '../catalog/types';
import { useCatalogChartData } from '../hooks/useCatalogChartData';
import type { ReactElement } from 'react';
import { getDashboardChartAreaSx } from '../theme/muiChartSlotProps';
import type { InspectionSummaryPart } from './triplet/inspectionSummaryParts';
import { MuiChartRenderer } from './MuiChartRenderer';
import { TripletPartChartView } from './TripletPartChartView';

type CatalogChartRendererProps = Readonly<{
  chartId: ChartId;
  height?: number;
  /** Renders one card-sized slice of an inspection triplet chart on the dashboard. */
  tripletPart?: InspectionSummaryPart;
}>;

export function CatalogChartRenderer({
  chartId,
  height,
  tripletPart,
}: CatalogChartRendererProps): ReactElement {
  const { isDetailedView, ctx, chart, result, definition, data } =
    useCatalogChartData(chartId);
  const chartHeight = height ?? ctx.tokens.heights[ctx.variant];

  if (!chart || !result || !definition || !data) {
    return <></>;
  }

  const renderChart = (): ReactElement => (
    <MuiChartRenderer
      chartId={chartId}
      viewModel={chart.viewModel}
      data={data}
      ctx={ctx}
      height={chartHeight}
    />
  );

  if (result.status === ChartResultStatus.loading || !result.filters) {
    return (
      <Box sx={{ ...getDashboardChartAreaSx() }}>
        <Spinner
          height={`${chartHeight}px`}
          position='static'
          reason='CatalogChartRenderer loading'
        />
      </Box>
    );
  }

  if (result.status === ChartResultStatus.error) {
    return <></>;
  }

  if (tripletPart && !isDetailedView) {
    return (
      <TripletPartChartView
        chartId={chartId}
        part={tripletPart}
        values={data.tripletValues ?? []}
      />
    );
  }

  if (isDetailedView) {
    return renderChart();
  }

  return renderChart();
}
